import { Temporal } from '@js-temporal/polyfill';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { useState, type FormEvent } from 'react';

import { openFile } from '../api/documents.ts';
import { meQuery } from '../api/me.ts';
import { dayLabel, dayWithYear } from '../api/quotes.ts';
import {
  monthLabel,
  recordVenueBill,
  renderStatement,
  shiftMonth,
  statementQuery,
  statementsQuery,
  type Statement,
  type StatementKind,
  type VenueBillInput,
} from '../api/statements.ts';
import { IconButton } from '../components/action-buttons.tsx';
import { icons } from '../components/icons.ts';
import { Button } from '../components/button.tsx';
import { Field } from '../components/field.tsx';
import { PageHeader } from '../components/page-header.tsx';
import { dollarsToMinorUnits, formatMinorUnits } from '../money.ts';

// CB-REQ VEN-11 at v1.990 (venue slice 4C): the venues' monthly statements, under Invoices. A
// resale venue's lists the month's invoices to it, earlier unpaid ones and the balance owing; a
// direct-bill venue's lists what the month's invoices owe it, commission and gear share apart,
// and the venue's own invoice once recorded. Live on screen; Make PDF keeps a dated copy. Money:
// seen only with prices (the API refuses otherwise).

const aud = (minor: string) => formatMinorUnits(minor, 'AUD');

export function StatementsScreen() {
  const me = useQuery(meQuery);
  const search = useSearch({ strict: false }) as { month?: string };
  const navigate = useNavigate();
  const thisMonth = me.data
    ? Temporal.Now.plainDateISO(me.data.tenant.defaultTz).toPlainYearMonth().toString()
    : null;
  const month = search.month ?? thisMonth;
  const statements = useQuery({ ...statementsQuery(month ?? ''), enabled: !!month });
  const go = (to: string) => void navigate({ to: '/statements', search: { month: to } });

  return (
    <div className="flex flex-col gap-6">
      <Link to="/invoices" className="link text-sm">
        ← Invoices
      </Link>
      <PageHeader
        kicker="Finance"
        title="Statements"
        meta={month ? monthLabel(month) : undefined}
        actions={
          month ? (
            <div className="flex gap-2">
              <IconButton
                icon={icons.previous}
                label="Previous month"
                onClick={() => go(shiftMonth(month, -1))}
              />
              <IconButton
                icon={icons.next}
                label="Next month"
                onClick={() => go(shiftMonth(month, 1))}
              />
            </div>
          ) : null
        }
      />
      {statements.isError ? (
        <p role="alert" className="alert">
          {statements.error.message}
        </p>
      ) : null}
      {statements.data && month ? (
        <StatementsTable statements={statements.data} month={month} />
      ) : null}
    </div>
  );
}

export function StatementsTable({ statements, month }: { statements: Statement[]; month: string }) {
  if (statements.length === 0) {
    return <p className="text-ink-soft">Nothing this month.</p>;
  }
  return (
    <table className="data-table">
      <caption className="sr-only">Each venue’s statement for {monthLabel(month)}</caption>
      <thead>
        <tr>
          <th scope="col">Venue</th>
          <th scope="col">Billing</th>
          <th scope="col" className="text-right">
            Invoices
          </th>
          <th scope="col" className="text-right">
            Owing or owed
          </th>
        </tr>
      </thead>
      <tbody>
        {statements.map((s) => (
          <tr key={`${s.venueId}-${s.kind}`}>
            <th scope="row">
              <Link
                to="/statements/$venueId/$month"
                params={{ venueId: s.venueId, month }}
                search={{ kind: s.kind }}
                className="link font-semibold"
              >
                {s.venueName}
              </Link>
            </th>
            <td>{s.kind === 'resale' ? 'Resale' : 'Direct bill'}</td>
            <td className="text-right tabular-nums">
              {s.resale ? s.resale.lines.length : (s.directBill?.lines.length ?? 0)}
            </td>
            <td className="text-right tabular-nums">
              {s.resale
                ? `${aud(s.resale.balanceOwingMinor)} owing`
                : `${aud(s.directBill!.totalOwedMinor)} owed`}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function StatementScreen() {
  const { venueId, month } = useParams({ from: '/authenticated/statements/$venueId/$month' });
  const { kind } = useSearch({ strict: false }) as { kind?: StatementKind };
  const me = useQuery(meQuery);
  const statement = useQuery(statementQuery(venueId, month, kind ?? 'resale'));
  const pdf = useMutation({
    mutationFn: async () => {
      const file = await renderStatement(venueId, month, kind ?? 'resale');
      await openFile(file.id);
    },
  });
  const mayRecord = me.data?.capabilities.includes('invoices.manage') ?? false;

  if (statement.isError) {
    return (
      <p role="alert" className="alert">
        {statement.error.message}
      </p>
    );
  }
  const s = statement.data;
  if (!s) return <p className="text-muted">Loading…</p>;
  return (
    <div className="flex flex-col gap-6">
      <Link to="/statements" search={{ month }} className="link text-sm">
        ← Statements
      </Link>
      <PageHeader
        kicker={s.kind === 'resale' ? 'Resale statement' : 'Commission statement'}
        title={s.venueName}
        meta={monthLabel(month)}
        actions={
          <Button disabled={pdf.isPending} onClick={() => pdf.mutate()}>
            {pdf.isPending ? 'Making…' : 'Make PDF'}
          </Button>
        }
      />
      {pdf.isError ? (
        <p role="alert" className="alert">
          {pdf.error.message}
        </p>
      ) : null}
      <StatementBody statement={s} />
      {s.directBill ? (
        <VenueBill statement={s} venueId={venueId} month={month} mayRecord={mayRecord} />
      ) : null}
    </div>
  );
}

export function StatementBody({ statement: s }: { statement: Statement }) {
  if (s.resale) {
    const r = s.resale;
    const rows = (lines: typeof r.lines) =>
      lines.map((l) => (
        <tr key={l.invoiceId}>
          <th scope="row">
            <Link to="/invoices/$invoiceId" params={{ invoiceId: l.invoiceId }} className="link">
              {l.reference}
            </Link>
            <span className="block text-xs font-normal text-muted">
              {[l.job, [l.requesterName, l.endClientName].filter(Boolean).join(', for ')]
                .filter(Boolean)
                .join(' · ')}
            </span>
          </th>
          <td className="text-sm">{dayLabel(l.issueDate)}</td>
          <td className="text-right tabular-nums">{aud(l.approvedMinor)}</td>
          <td className="text-right tabular-nums">
            {l.tradeDiscountMinor === '0' ? '—' : `−${aud(l.tradeDiscountMinor)}`}
          </td>
          <td className="text-right tabular-nums">{aud(l.sellingServiceMinor)}</td>
          <td className="text-right tabular-nums">{aud(l.netMinor)}</td>
          <td className="text-right tabular-nums">{aud(l.taxMinor)}</td>
          <td className="text-right tabular-nums">{aud(l.totalMinor)}</td>
          <td className="text-sm">
            {l.paid
              ? 'Paid'
              : l.owingMinor !== l.totalMinor
                ? `Owing ${aud(l.owingMinor)}`
                : `Due ${dayLabel(l.dueDate)}`}
          </td>
        </tr>
      ));
    const head = (
      <thead>
        <tr>
          <th scope="col">Invoice</th>
          <th scope="col">Issued</th>
          <th scope="col" className="text-right">
            Approved
          </th>
          <th scope="col" className="text-right">
            Trade discount
          </th>
          <th scope="col" className="text-right">
            Selling service
          </th>
          <th scope="col" className="text-right">
            Net
          </th>
          <th scope="col" className="text-right">
            GST
          </th>
          <th scope="col" className="text-right">
            Total
          </th>
          <th scope="col">
            <span className="sr-only">Paid</span>
          </th>
        </tr>
      </thead>
    );
    return (
      <section className="flex flex-col gap-4">
        <p className="text-sm text-ink-soft">To {r.billedToName}</p>
        {r.lines.length > 0 ? (
          <table className="data-table">
            <caption className="sr-only">Issued this month</caption>
            {head}
            <tbody>{rows(r.lines)}</tbody>
          </table>
        ) : (
          <p className="text-ink-soft">No invoices this month.</p>
        )}
        {r.earlier.length > 0 ? (
          <>
            <h2 className="section-title">Still owing from earlier</h2>
            <table className="data-table">
              <caption className="sr-only">Still owing from earlier</caption>
              {head}
              <tbody>{rows(r.earlier)}</tbody>
            </table>
          </>
        ) : null}
        {r.adjustments.length > 0 ? (
          <>
            <h2 className="section-title">Adjustment notes</h2>
            <CreditRows credits={r.adjustments} kind="resale" />
          </>
        ) : null}
        <p className="ml-auto text-lg font-extrabold tabular-nums">
          Balance owing {aud(r.balanceOwingMinor)}
        </p>
        <AllocationFoot statement={s} />
      </section>
    );
  }
  const d = s.directBill!;
  return (
    <section className="flex flex-col gap-4">
      {d.lines.length > 0 ? (
        <table className="data-table">
          <caption className="sr-only">What this month’s invoices owe the venue</caption>
          <thead>
            <tr>
              <th scope="col">Invoice</th>
              <th scope="col">Client</th>
              <th scope="col" className="text-right">
                Net
              </th>
              <th scope="col" className="text-right">
                Commission
              </th>
              <th scope="col" className="text-right">
                Gear share
              </th>
            </tr>
          </thead>
          <tbody>
            {d.lines.map((l) => (
              <tr key={l.invoiceId}>
                <th scope="row">
                  <Link
                    to="/invoices/$invoiceId"
                    params={{ invoiceId: l.invoiceId }}
                    className="link"
                  >
                    {l.reference}
                  </Link>
                  <span className="block text-xs font-normal text-muted">{l.job}</span>
                </th>
                <td>{l.clientName}</td>
                <td className="text-right tabular-nums">{aud(l.netMinor)}</td>
                <td className="text-right tabular-nums">{aud(l.commissionMinor)}</td>
                <td className="text-right tabular-nums">{aud(l.venueGearShareMinor)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="text-ink-soft">No invoices this month.</p>
      )}
      {d.credits.length > 0 ? (
        <>
          <h2 className="section-title">Credits</h2>
          <CreditRows credits={d.credits} kind="direct_bill" />
        </>
      ) : null}
      <dl className="ml-auto grid w-full max-w-sm grid-cols-[1fr_auto] gap-x-6 gap-y-1">
        <dt className="text-ink-soft">Commission, ex GST</dt>
        <dd className="text-right tabular-nums">{aud(d.commissionMinor)}</dd>
        <dt className="text-ink-soft">Gear share, ex GST</dt>
        <dd className="text-right tabular-nums">{aud(d.venueGearShareMinor)}</dd>
        <dt className="font-extrabold">Owed to the venue</dt>
        <dd className="text-right text-lg font-extrabold tabular-nums">{aud(d.totalOwedMinor)}</dd>
      </dl>
      <AllocationFoot statement={s} />
    </section>
  );
}

/**
 * FIN-02 at v1.993: the month's credits on the venue's invoices — on resale what each takes off
 * the balance, on direct bill what it takes off the commission and gear share owed.
 */
function CreditRows({
  credits,
  kind,
}: {
  credits: NonNullable<Statement['resale']>['adjustments'];
  kind: 'resale' | 'direct_bill';
}) {
  return (
    <table className="data-table">
      <caption className="sr-only">
        {kind === 'resale' ? 'Adjustment notes this month' : 'Credits this month'}
      </caption>
      <thead>
        <tr>
          <th scope="col">{kind === 'resale' ? 'Adjustment note' : 'Credit'}</th>
          <th scope="col">Issued</th>
          <th scope="col" className="text-right">
            Net
          </th>
          <th scope="col" className="text-right">
            {kind === 'resale' ? 'Total' : 'Commission'}
          </th>
          {kind === 'direct_bill' ? (
            <th scope="col" className="text-right">
              Gear share
            </th>
          ) : null}
        </tr>
      </thead>
      <tbody>
        {credits.map((c) => (
          <tr key={c.creditNoteId}>
            <th scope="row">
              <Link
                to="/credit-notes/$creditNoteId"
                params={{ creditNoteId: c.creditNoteId }}
                className="link"
              >
                {c.reference}
              </Link>
              <span className="block text-xs font-normal text-muted">{c.invoiceReference}</span>
            </th>
            <td className="text-sm">{dayLabel(c.issueDate)}</td>
            <td className="text-right tabular-nums">−{aud(c.netMinor)}</td>
            <td className="text-right tabular-nums">
              −{aud(kind === 'resale' ? c.totalMinor : c.commissionMinor)}
            </td>
            {kind === 'direct_bill' ? (
              <td className="text-right tabular-nums">−{aud(c.venueGearShareMinor)}</td>
            ) : null}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** VEN-11 at v1.992: the venue's complimentary allocation, its period on the month's last day. */
function AllocationFoot({ statement: s }: { statement: Statement }) {
  const a = s.allocation;
  if (!a) return null;
  const last = Temporal.PlainDate.from(a.periodEnd).subtract({ days: 1 }).toString();
  return (
    <p className="ml-auto text-sm text-ink-soft">
      {`Complimentary allocation, ${dayWithYear(a.periodStart)} – ${dayWithYear(last)}: ${aud(a.allowanceMinor)} allowed, ${aud(a.usedMinor)} used, ${aud(a.leftMinor)} left.`}
    </p>
  );
}

function VenueBill({
  statement,
  venueId,
  month,
  mayRecord,
}: {
  statement: Statement;
  venueId: string;
  month: string;
  mayRecord: boolean;
}) {
  const queryClient = useQueryClient();
  const [mismatch, setMismatch] = useState(false);
  const record = useMutation({
    mutationFn: (input: VenueBillInput) => recordVenueBill(venueId, month, input),
    onSuccess: async (result) => {
      setMismatch(result.mismatch);
      await queryClient.invalidateQueries({ queryKey: ['statements', month] });
    },
  });
  const bill = statement.directBill!.bill;
  if (bill) {
    return (
      <section className="flex flex-col gap-1" aria-labelledby="venue-bill">
        <h2 id="venue-bill" className="section-title">
          Billed by the venue
        </h2>
        <p className="text-sm">
          {bill.venueInvoiceNumber} · {dayLabel(bill.invoiceDate)} · {aud(bill.amountMinor)} + GST{' '}
          {aud(bill.gstMinor)}
        </p>
        {mismatch || bill.amountMinor !== bill.statementTotalMinor ? (
          <p role="status" className="text-sm font-semibold text-accent-2-700">
            Not the statement’s {aud(bill.statementTotalMinor)}.
          </p>
        ) : null}
      </section>
    );
  }
  if (!mayRecord) return null;
  return (
    <VenueBillForm
      pending={record.isPending}
      error={record.error}
      onSubmit={(input) => record.mutate(input)}
    />
  );
}

/** The venue's own invoice: its number, date, and amount ex GST with its GST, in dollars. */
export function VenueBillForm({
  pending,
  error,
  onSubmit,
}: {
  pending: boolean;
  error: Error | null;
  onSubmit: (input: VenueBillInput) => void;
}) {
  const [number, setNumber] = useState('');
  const [date, setDate] = useState('');
  const [amount, setAmount] = useState('');
  const [gst, setGst] = useState('');
  const [problem, setProblem] = useState<string | null>(null);

  function submit(event: FormEvent) {
    event.preventDefault();
    const amountMinor = dollarsToMinorUnits(amount);
    const gstMinor = dollarsToMinorUnits(gst || '0');
    if (!number.trim() || !date) return setProblem('Give its number and date.');
    if (amountMinor === null || gstMinor === null) {
      return setProblem('Amounts are in dollars, like 740.00.');
    }
    setProblem(null);
    onSubmit({ venueInvoiceNumber: number.trim(), invoiceDate: date, amountMinor, gstMinor });
  }

  const shown = problem ?? error?.message ?? null;
  return (
    <form onSubmit={submit} className="panel flex flex-col gap-3" aria-label="The venue’s invoice">
      <h2 className="section-title">Record the venue’s invoice</h2>
      {shown ? (
        <p role="alert" className="alert">
          {shown}
        </p>
      ) : null}
      <div className="grid gap-3 sm:grid-cols-2">
        <Field
          label="Invoice number"
          value={number}
          onChange={(e) => setNumber(e.currentTarget.value)}
        />
        <Field
          label="Invoice date"
          type="date"
          value={date}
          onChange={(e) => setDate(e.currentTarget.value)}
        />
        <Field
          label="Amount ex GST ($)"
          inputMode="decimal"
          value={amount}
          onChange={(e) => setAmount(e.currentTarget.value)}
        />
        <Field
          label="GST ($)"
          inputMode="decimal"
          value={gst}
          onChange={(e) => setGst(e.currentTarget.value)}
        />
      </div>
      <Button type="submit" disabled={pending} className="self-start">
        {pending ? 'Saving…' : 'Save'}
      </Button>
    </form>
  );
}
